import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Music, ListMusic, Play, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchSongs, fetchSetlists } from "@/lib/api";
import { formatTotal } from "@/lib/format";

export default function Dashboard() {
  const [songs, setSongs] = useState([]);
  const [setlists, setSetlists] = useState([]);

  useEffect(() => {
    fetchSongs().then(setSongs).catch(() => {});
    fetchSetlists().then(setSetlists).catch(() => {});
  }, []);

  const totalDuration = songs.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      {/* Hero */}
      <section
        className="relative overflow-hidden rounded-xl border border-[#2D2D2D] bg-[#1A1A1A] mb-10"
        data-testid="dashboard-hero"
      >
        <div
          className="absolute inset-0 bg-grid opacity-30 pointer-events-none"
          aria-hidden
        />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#00F0FF]/10 blur-3xl pointer-events-none" />
        <div className="relative px-6 sm:px-10 py-10 sm:py-14">
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF] mb-3">
            // controle de palco
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            Seu repertório, <br />
            <span className="text-[#00F0FF]">no controle.</span>
          </h1>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mb-7 leading-relaxed">
            Cadastre músicas, monte setlists, organize equipamentos por faixa e
            entre em modo performance com fontes gigantes e checklist ao vivo.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/songs">
              <Button
                className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold h-11 px-5"
                data-testid="hero-songs-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Nova música
              </Button>
            </Link>
            <Link to="/setlists">
              <Button
                variant="outline"
                className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#1A1A1A] h-11 px-5"
                data-testid="hero-setlists-btn"
              >
                Ver Setlists <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
        <StatCard
          testid="stat-songs"
          icon={Music}
          label="Músicas cadastradas"
          value={songs.length}
        />
        <StatCard
          testid="stat-setlists"
          icon={ListMusic}
          label="Setlists criadas"
          value={setlists.length}
        />
        <StatCard
          testid="stat-duration"
          icon={Play}
          label="Duração total no banco"
          value={formatTotal(totalDuration)}
          mono
        />
      </section>

      {/* Recent setlists */}
      <section className="mb-12" data-testid="recent-setlists-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
            Setlists recentes
          </h2>
          <Link
            to="/setlists"
            className="text-sm text-[#00F0FF] hover:underline font-medium"
            data-testid="view-all-setlists"
          >
            Ver todas
          </Link>
        </div>
        {setlists.length === 0 ? (
          <EmptyState
            title="Nenhum setlist ainda"
            description="Crie seu primeiro setlist e adicione músicas do seu banco."
            ctaLabel="Criar setlist"
            ctaTo="/setlists"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {setlists.slice(0, 6).map((s) => {
              const totalSec = s.song_ids
                .map((id) => songs.find((song) => song.id === id))
                .filter(Boolean)
                .reduce((acc, song) => acc + (song.duration_seconds || 0), 0);
              return (
                <Link
                  key={s.id}
                  to={`/setlists/${s.id}`}
                  data-testid={`setlist-card-${s.id}`}
                  className="group block p-5 rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] hover:border-[#00F0FF] transition-all duration-150 hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-lg leading-tight">
                      {s.name}
                    </h3>
                    <span className="font-mono text-xs text-[#00F0FF] bg-[#00F0FF]/10 px-2 py-1 rounded">
                      {s.song_ids.length} faixas
                    </span>
                  </div>
                  {s.venue && (
                    <p className="text-zinc-500 text-sm mb-3">{s.venue}</p>
                  )}
                  <div className="font-mono text-xs text-zinc-400">
                    {formatTotal(totalSec)}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, mono, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-5 rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] flex items-center gap-4 hover:border-[#00F0FF]/40 transition-all duration-150"
    >
      <div className="w-12 h-12 rounded-md bg-[#00F0FF]/10 flex items-center justify-center text-[#00F0FF]">
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="flex flex-col">
        <span className="text-xs uppercase tracking-wider text-zinc-500 font-medium">
          {label}
        </span>
        <span
          className={`text-2xl font-bold ${mono ? "font-mono text-[#00F0FF]" : ""}`}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ title, description, ctaLabel, ctaTo }) {
  return (
    <div className="rounded-lg border border-dashed border-[#2D2D2D] bg-[#1A1A1A]/60 p-10 text-center">
      <p className="text-lg font-semibold mb-1">{title}</p>
      <p className="text-zinc-500 text-sm mb-5">{description}</p>
      <Link to={ctaTo}>
        <Button className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold">
          {ctaLabel}
        </Button>
      </Link>
    </div>
  );
}
